import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Truck, Wrench, CheckCircle2, Circle, Star, MapPin, Phone } from "lucide-react";
import api from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import { Loader, StatusBadge } from "@/components/common";
import { ORDER_STATUS, ORDER_STEPS, DELIVERY_STATUS, INSTALL_STATUS, PAYMENT_STATUS } from "@/lib/labels";
import ReviewModal from "@/components/ReviewModal";

export default function OrderDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [review, setReview] = useState(null);

  const load = useCallback(() => { api.get(`/orders/${id}`).then((r) => setData(r.data)); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader />;
  const { order, delivery, installations } = data;
  const currentStep = ORDER_STEPS.indexOf(order.status);

  return (
    <div className="max-w-3xl">
      <Link to="/commandes" className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft className="w-4 h-4" /> Mes commandes</Link>
      <div className="flex items-center gap-3 flex-wrap mb-1">
        <h1 className="font-display text-3xl font-extrabold text-[#0F172A]">{order.order_number}</h1>
        <StatusBadge map={ORDER_STATUS} value={order.status} />
        <StatusBadge map={PAYMENT_STATUS} value={order.payment_status} />
      </div>
      <p className="text-sm text-slate-500 mb-5">Passée le {formatDate(order.created_at)}</p>

      {/* Global tracking */}
      <Section title="Suivi de la commande">
        <div className="space-y-0">
          {ORDER_STEPS.map((s, i) => {
            const done = i <= currentStep;
            return (
              <div key={s} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  {done ? <CheckCircle2 className="w-5 h-5 text-[#16A34A]" /> : <Circle className="w-5 h-5 text-slate-300" />}
                  {i < ORDER_STEPS.length - 1 && <div className={`w-0.5 h-6 ${i < currentStep ? "bg-[#16A34A]" : "bg-slate-200"}`} />}
                </div>
                <span className={`text-sm pb-3 ${done ? "font-semibold text-slate-800" : "text-slate-400"}`}>{ORDER_STATUS[s]?.label}</span>
              </div>
            );
          })}
        </div>
      </Section>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Delivery */}
        {delivery && (
          <Section title={<span className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#C85A32]" /> Livraison</span>}>
            <StatusBadge map={DELIVERY_STATUS} value={delivery.status} />
            <p className="text-sm text-slate-600 mt-2 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {order.zone}</p>
            {delivery.proof && (
              <div className="mt-2 text-xs text-slate-500">
                <p>Reçu par : {delivery.proof.receiver_name}</p>
                {delivery.proof.photo && <img src={delivery.proof.photo} alt="preuve" className="w-24 h-24 object-cover rounded-lg mt-1" />}
              </div>
            )}
            {delivery.status === "livree" && (
              <button onClick={() => setReview({ type: "delivery", id: delivery.id })} className="mt-3 text-sm font-semibold text-[#C85A32] flex items-center gap-1" data-testid="review-delivery-btn">
                <Star className="w-4 h-4" /> Évaluer la livraison
              </button>
            )}
          </Section>
        )}

        {/* Installations */}
        <Section title={<span className="flex items-center gap-2"><Wrench className="w-4 h-4 text-[#C85A32]" /> Installation</span>}>
          {installations.length === 0 ? <p className="text-sm text-slate-400">Aucune installation liée.</p> : installations.map((ins) => (
            <div key={ins.id} className="mb-2">
              <p className="text-sm font-semibold">{ins.product_name}</p>
              <StatusBadge map={INSTALL_STATUS} value={ins.status} />
              {(ins.status === "terminee" || ins.status === "cloturee") && (
                <button onClick={() => setReview({ type: "installation", id: ins.id })} className="mt-2 block text-sm font-semibold text-[#C85A32]" data-testid="review-install-btn">
                  ⭐ Évaluer l'installation
                </button>
              )}
            </div>
          ))}
        </Section>
      </div>

      {/* Items / invoice */}
      <Section title="Détail (facture)">
        <div className="space-y-2">
          {order.items.map((i) => (
            <div key={i.product_id} className="flex justify-between text-sm">
              <span className="text-slate-600">{i.quantity}× {i.name}</span>
              <span className="font-num font-semibold">{formatFCFA(i.price * i.quantity)}</span>
            </div>
          ))}
          <div className="border-t pt-2 flex justify-between text-sm"><span className="text-slate-500">Livraison</span><span className="font-num">{formatFCFA(order.delivery_fee)}</span></div>
          <div className="flex justify-between font-bold"><span>Total</span><span className="font-num text-[#C85A32]">{formatFCFA(order.total)}</span></div>
        </div>
      </Section>

      {review && (
        <ReviewModal
          targetType={review.type} targetId={review.id} onClose={() => setReview(null)} onDone={load}
          subFields={review.type === "delivery"
            ? [{ key: "delai", label: "Respect du délai" }, { key: "livreur", label: "Comportement du livreur" }]
            : [{ key: "qualite", label: "Qualité du travail" }, { key: "rdv", label: "Respect du rendez-vous" }, { key: "pro", label: "Professionnalisme" }]}
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
      <h2 className="font-display text-lg font-bold mb-3">{title}</h2>
      {children}
    </div>
  );
}
